import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { ChevronDown, Menu, Leaf, LogOut, LogIn, User, Cloud, Check, Plus, LayoutDashboard, Flame, Award, Eye, Sticker, Target } from "lucide-react";
import { stepsThisWeek } from "../lib/streak";
import { ownedCount, STICKERS } from "../data/stickers";
import { computeStreak } from "../lib/streak";
import { toast } from "sonner";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "./ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import { modules } from "../data/mock";
import { useProgress } from "../context/ProgressContext";
import { useAuth } from "../context/AuthContext";
import Panda from "./Panda";
import CheerBadge from "./CheerBadge";
import SyncChip from "./SyncChip";

export const Logo = ({ size = "md" }) => {
  const letters = ["C", "r", "e", "d", "i", "t"];
  const colors = ["#e5356f", "#f7931e", "#63c79a", "#1b8bc9", "#7d5fb2", "#f3c932"];
  const big = size === "lg";
  return (
    <Link to="/" className="inline-flex flex-col items-center leading-none select-none" data-testid="logo">
      <span className={`font-serif italic font-bold text-[#1b8bc9] ${big ? "text-3xl" : "text-sm"}`} style={{ fontFamily: "Literata, serif" }}>
        Teach your child
      </span>
      <span className={`font-logo ${big ? "text-6xl -mt-1" : "text-2xl -mt-0.5"} tracking-tight`}>
        {letters.map((l, i) => (
          <span key={i} style={{ color: colors[i], display: "inline-block", transform: `rotate(${i % 2 ? 4 : -4}deg)` }}>
            {l}
          </span>
        ))}
      </span>
    </Link>
  );
};

const navLink = "font-bold text-[#1b8bc9] hover:text-[#e5356f] transition-colors text-[17px] flex items-center gap-1";

const Header = () => {
  const navigate = useNavigate();
  const { panda, bamboo, children, activeChildId, switchChild, activity, activeChild, followedChildren, weeklyGoal, stepLog } = useProgress();
  const streak = computeStreak(activity);
  const weekSteps = stepsThisWeek(stepLog);
  const goalMet = weeklyGoal ? weekSteps >= weeklyGoal : false;
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);

  const handleLogout = () => {
    logout();
    toast("Logged out. Progress on this device was saved to your account.");
    navigate("/");
  };

  const parentLinks = [
    { to: "/family", label: "Family dashboard" },
    { to: "/certificates", label: "Certificate wall" },
    { to: "/parents/fundamentals", label: "Credit fundamentals" },
    { to: "/parents/facts", label: "Corrected facts sheet" },
    { to: "/parents/glossary", label: "Glossary" },
    { to: "/parents/resources", label: "Resources" },
    { to: "/parents/worksheets", label: "Printable worksheets" },
  ];

  return (
    <header className="sticky top-0 z-50 bg-white/95 backdrop-blur border-b border-[#e9f9fd] shadow-sm" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-[76px] flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="hidden sm:flex items-center justify-center w-11 h-11 rounded-full bg-[#63c79a] text-white shadow">
            <Leaf size={22} />
          </div>
          <Logo />
        </div>

        <nav className="hidden lg:flex items-center gap-8">
          <DropdownMenu>
            <DropdownMenuTrigger className={navLink} data-testid="nav-lessons">
              Lessons <ChevronDown size={18} />
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-72 rounded-2xl p-2 border-[#e9f9fd] shadow-xl">
              <DropdownMenuItem onClick={() => navigate("/lessons")} className="rounded-xl font-semibold text-[#1b8bc9] cursor-pointer">
                All lessons
              </DropdownMenuItem>
              {modules.map((m) => (
                <DropdownMenuItem key={m.id} onClick={() => navigate(`/lessons/${m.slug}`)} className="rounded-xl cursor-pointer gap-3">
                  <span className="w-3 h-3 rounded-full" style={{ background: m.color }} />
                  <span className="flex-1">{m.short}</span>
                  <span className="text-xs text-gray-500">Ages {m.ages}</span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <DropdownMenu>
            <DropdownMenuTrigger className={navLink} data-testid="nav-parents">
              Parents <ChevronDown size={18} />
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-64 rounded-2xl p-2 border-[#e9f9fd] shadow-xl">
              {parentLinks.map((l) => (
                <DropdownMenuItem key={l.to} onClick={() => navigate(l.to)} className="rounded-xl cursor-pointer">
                  {l.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <NavLink to="/about" className={navLink} data-testid="nav-about">
            About
          </NavLink>

          {panda?.name ? (
            <div className="flex items-center gap-1">
            <SyncChip />
            <CheerBadge child={activeChild} />
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center gap-2 bg-[#e9f9fd] rounded-full pl-1 pr-3 py-1 hover:bg-[#d3f2fa] transition-colors" data-testid="nav-panda">
                <Panda size={34} accessory={panda.accessory} scarf={panda.scarf} />
                <span className="font-bold text-[#0c4a6e] text-sm">{panda.name}</span>
                <span className="text-xs font-bold text-[#2e8b57]" data-testid="nav-bamboo">{bamboo} bamboo</span>
                <span
                  className={`inline-flex items-center gap-0.5 text-xs font-bold rounded-full px-2 py-0.5 ${streak.activeThisWeek ? "bg-[#f7931e] text-white" : streak.weeks > 0 ? "bg-[#fff8e1] text-[#8a6d00]" : "bg-white text-gray-400"}`}
                  title={streak.activeThisWeek ? `${streak.weeks}-week streak - active this week` : streak.weeks > 0 ? `${streak.weeks}-week streak at risk - do a step this week` : "Do a lesson step to start a weekly streak"}
                  data-testid="nav-streak"
                >
                  <Flame size={12} fill={streak.activeThisWeek ? "#fff" : "none"} /> {streak.weeks}w
                </span>
                {weeklyGoal && (
                  <span className={`inline-flex items-center gap-0.5 text-xs font-bold rounded-full px-2 py-0.5 ${goalMet ? "bg-[#63c79a] text-white" : "bg-white text-[#0c4a6e]"}`} title={goalMet ? `Weekly goal reached: ${weekSteps}/${weeklyGoal} steps` : `${weekSteps} of ${weeklyGoal} lesson steps this week`} data-testid="nav-goal">
                    {goalMet ? <Check size={12} /> : <Target size={12} />} {weekSteps}/{weeklyGoal}
                  </span>
                )}
                <ChevronDown size={16} className="text-[#1b8bc9]" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64 rounded-2xl p-2 border-[#e9f9fd] shadow-xl">
                <p className="px-3 py-1 text-xs uppercase tracking-wider text-gray-500 font-bold">Who's learning?</p>
                {children.map((c) => (
                  <DropdownMenuItem key={c.id} onClick={() => { switchChild(c.id); toast.success(`${c.panda.name} is learning now`); }} className="rounded-xl cursor-pointer gap-2" data-testid={`switch-child-${c.id}`}>
                    <Panda size={26} accessory={c.panda.accessory} scarf={c.panda.scarf} />
                    <span className="flex-1 font-semibold">{c.panda.name}</span>
                    {c.id === activeChildId && <Check size={16} className="text-[#2e8b57]" />}
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/panda?new=1")} className="rounded-xl cursor-pointer gap-2 text-[#e5356f] font-semibold" data-testid="nav-add-child"><Plus size={16} /> Add a child</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/family")} className="rounded-xl cursor-pointer gap-2 font-semibold" data-testid="nav-family"><LayoutDashboard size={16} /> Family dashboard</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/certificates")} className="rounded-xl cursor-pointer gap-2 font-semibold" data-testid="nav-certificates"><Award size={16} /> Certificate wall</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/stickers")} className="rounded-xl cursor-pointer gap-2 font-semibold" data-testid="nav-stickers"><Sticker size={16} /> Sticker book <span className="ml-auto text-xs text-[#f7931e] font-bold">{ownedCount(activeChild)}/{STICKERS.length}</span></DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/panda")} className="rounded-xl cursor-pointer gap-2"><User size={16} /> Edit {panda.name}'s panda</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            </div>
          ) : user && followedChildren.length > 0 ? (
            <div className="flex items-center gap-2">
            <SyncChip />
            <Link to="/family" className="btn-pill bg-[#7d5fb2] hover:bg-[#6a4fa0] text-white inline-flex items-center gap-1" data-testid="nav-following">
              <Eye size={16} /> Children you follow
            </Link>
            </div>
          ) : (
            <Link to="/panda" className="btn-pill bg-[#e5356f] hover:bg-[#c92a5e] text-white" data-testid="nav-get-started">
              Get started
            </Link>
          )}

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger className="w-11 h-11 rounded-full bg-[#7d5fb2] text-white font-bold flex items-center justify-center hover:bg-[#6a4fa0] transition-colors" aria-label="Account menu" data-testid="nav-account">
                {user.name.charAt(0).toUpperCase()}
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-60 rounded-2xl p-2 border-[#e9f9fd] shadow-xl">
                <div className="px-3 py-2">
                  <p className="font-bold text-[#0c4a6e] truncate">{user.name}</p>
                  <p className="text-xs text-gray-500 truncate">{user.email}</p>
                  <p className="text-xs text-[#2e8b57] font-bold mt-1 flex items-center gap-1"><Cloud size={12} /> Progress synced</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/panda")} className="rounded-xl cursor-pointer gap-2"><User size={16} /> My panda</DropdownMenuItem>
                <DropdownMenuItem onClick={handleLogout} className="rounded-xl cursor-pointer gap-2 text-[#e5356f]" data-testid="nav-logout"><LogOut size={16} /> Log out</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Link to="/login" className="font-bold text-[#1b8bc9] hover:text-[#e5356f] inline-flex items-center gap-1" data-testid="nav-login">
              <LogIn size={18} /> Log in
            </Link>
          )}
        </nav>

        <div className="lg:hidden flex items-center gap-2">
          <SyncChip variant="mobile" />
          {panda?.name && <CheerBadge child={activeChild} variant="mobile" />}
          <Link to={panda?.name ? "/panda" : user && followedChildren.length > 0 ? "/family" : "/panda"} className="btn-pill bg-[#e5356f] hover:bg-[#c92a5e] text-white text-sm px-4 py-2" data-testid="nav-get-started-mobile">
            {panda?.name ? panda.name : user && followedChildren.length > 0 ? "Following" : "Get started"}
          </Link>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger className="p-2 rounded-full text-[#1b8bc9] hover:bg-[#e9f9fd]" aria-label="Open menu" data-testid="mobile-menu-button">
              <Menu size={28} />
            </SheetTrigger>
            <SheetContent side="right" className="w-80 overflow-y-auto">
              <div className="mt-6 flex flex-col gap-1">
                {children.length > 0 && (
                  <>
                    <p className="px-3 text-xs uppercase tracking-wider text-gray-500 font-bold">Who's learning?</p>
                    {children.map((c) => (
                      <button key={c.id} onClick={() => { switchChild(c.id); setOpen(false); }} className={`px-3 py-2 rounded-xl flex items-center gap-2 text-left ${c.id === activeChildId ? "bg-[#e9f9fd]" : "hover:bg-[#f7fdfe]"}`}>
                        <Panda size={26} accessory={c.panda.accessory} scarf={c.panda.scarf} />
                        <span className="font-semibold flex-1">{c.panda.name}</span>
                        {c.id === activeChildId && <Check size={16} className="text-[#2e8b57]" />}
                      </button>
                    ))}
                    <Link to="/panda?new=1" onClick={() => setOpen(false)} className="px-3 py-2 rounded-xl text-[#e5356f] font-bold flex items-center gap-2"><Plus size={16} /> Add a child</Link>
                    <Link to="/stickers" onClick={() => setOpen(false)} className="px-3 py-2 rounded-xl text-[#f7931e] font-bold flex items-center gap-2" data-testid="mobile-stickers"><Sticker size={16} /> Sticker book</Link>
                  </>
                )}
                <p className="px-3 mt-4 text-xs uppercase tracking-wider text-gray-500 font-bold">Lessons</p>
                <Link to="/lessons" onClick={() => setOpen(false)} className="px-3 py-2 rounded-xl font-bold text-[#1b8bc9] hover:bg-[#e9f9fd]">All lessons</Link>
                {modules.map((m) => (
                  <Link key={m.id} to={`/lessons/${m.slug}`} onClick={() => setOpen(false)} className="px-3 py-2 rounded-xl hover:bg-[#e9f9fd] flex items-center gap-3">
                    <span className="w-3 h-3 rounded-full" style={{ background: m.color }} />
                    {m.short} <span className="text-xs text-gray-500 ml-auto">Ages {m.ages}</span>
                  </Link>
                ))}
                <p className="px-3 mt-4 text-xs uppercase tracking-wider text-gray-500 font-bold">Parents</p>
                {parentLinks.map((l) => (
                  <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="px-3 py-2 rounded-xl hover:bg-[#e9f9fd]">{l.label}</Link>
                ))}
                <Link to="/about" onClick={() => setOpen(false)} className="px-3 py-2 mt-4 rounded-xl font-bold text-[#1b8bc9] hover:bg-[#e9f9fd]">About the book</Link>
                <div className="mt-4 pt-4 border-t border-[#e9f9fd]">
                  {user ? (
                    <div className="px-3">
                      <p className="font-bold text-[#0c4a6e]">{user.name}</p>
                      <p className="text-xs text-gray-500">{user.email}</p>
                      <button onClick={() => { setOpen(false); handleLogout(); }} className="mt-3 inline-flex items-center gap-2 font-bold text-[#e5356f]" data-testid="mobile-logout"><LogOut size={16} /> Log out</button>
                    </div>
                  ) : (
                    <Link to="/login" onClick={() => setOpen(false)} className="px-3 py-2 rounded-xl font-bold text-[#1b8bc9] hover:bg-[#e9f9fd] inline-flex items-center gap-2" data-testid="mobile-login"><LogIn size={16} /> Log in / Sign up</Link>
                  )}
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default Header;
